import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../../shared/components/Layout';
import AlertMessages from '@/components/AlertMessages';
import getOrderHistoryDetail from '../../api/getOrderHistoryDetail';

type Product = {
  id: number;
  itemCode: string;
  name: string;
  type: string;
  lensType: string;
};

type OrderClItem = {
  id: number;
  qty: number;
  eyeType: string;
  fc: string;
  tp: string;
  dia: string;
  cyl: string | null;
  product: Product;
};

type OrderPromoCareItem = {
  id: number;
  qty: number;
  quantityUnit: string;
  product: Product;
};

type OrderDetail = {
  id: number;
  status: string;
  orderCode: string;
  orderedAt: string;
  arrivalDate: string | null;
  arrivalTimeZone: string;
  orderClassification: string | null;
  custNameKana: string | null;
  shipDate: string | null;
  orderType: string;
  orderClItems: OrderClItem[];
  orderPromoCareItems: OrderPromoCareItem[];
};

const Detail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [alertMessageOpen, setAlertMessageOpen] = useState<boolean>(false);
  const [alertMessages, setAlertMessages] = useState<string[]>([]);
  const [orderDetail, setOrderDetail] = useState<OrderDetail | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchOrderDetail = async () => {
      if (!id) {
        navigate('/facility/orders/history');
        return;
      }

      try {
        const response = await getOrderHistoryDetail(id);
        setOrderDetail(response.data.order);
        setLoading(false);
      } catch (error) {
        console.error('注文詳細の取得に失敗しました:', error);
        setAlertMessages(['注文詳細の取得に失敗しました。']);
        setAlertMessageOpen(true);
        setLoading(false);
      }
    };

    fetchOrderDetail();
  }, [id, navigate]);

  const handleBack = () => {
    navigate('/facility/orders/history');
  };

  if (loading) {
    return (
      <Layout>
        <main className="wrapper">
          <section className="sec-contact">
            <div className="inner">
              <div className="loading">読み込み中...</div>
            </div>
          </section>
        </main>
      </Layout>
    );
  }

  if (!orderDetail) {
    return (
      <Layout>
        <main className="wrapper">
          <section className="sec-contact">
            <div className="inner">
              <h1 className="c-ttl__01 mbsp-18">注文詳細</h1>
              <div className="no-data">
                <p>注文情報が見つかりませんでした。</p>
                <button onClick={handleBack} className="btn-back">
                  一覧に戻る
                </button>
              </div>
            </div>
          </section>
        </main>
      </Layout>
    );
  }

  // orderClItemsの中にlensTypeが'astigma'の商品が1つでもあるかチェック
  const hasAstigmaProduct = orderDetail?.orderClItems?.some(
    item => item.product?.lensType === 'astigma'
  ) || false;

  return (
    <React.Fragment>
      <Layout>
        <main id="page-order-detail" className="wrapper">
          <section className="sec-order-detail sec-contact">
            <div className="inner">
              <h1 className="c-ttl__01 mbsp-18">注文詳細</h1>
              
              {alertMessageOpen && (
                <AlertMessages
                  open={alertMessageOpen}
                  messages={alertMessages}
                />
              )}

              <div className="order-detail-content">
                {/* 基本情報セクション */}
                <div className="detail-section">
                  <h2 className="section-title">基本情報</h2>
                  <dl className="detail-list">

                    {/* TODO: 鈴木さんからコメントアウト外すのを許可えたら外す */}
                    {/* <div className="detail-item">
                      <dt>状態</dt>
                      <dd>{orderDetail.status}</dd>
                    </div> */}
                    <div className="detail-item">
                      <dt>注文コード</dt>
                      <dd>{orderDetail.orderCode}</dd>
                    </div>
                    <div className="detail-item">
                      <dt>注文日時</dt>
                      <dd>{orderDetail.orderedAt}</dd>
                    </div>
                    <div className="detail-item">
                      <dt>ご希望日</dt>
                      <dd>{orderDetail.arrivalDate || '-'}</dd>
                    </div>
                    <div className="detail-item">
                      <dt>ご希望時間帯</dt>
                      <dd>{orderDetail.arrivalTimeZone}</dd>
                    </div>
                    <div className="detail-item">
                      <dt>注文区分</dt>
                      <dd>{orderDetail.orderClassification || '-'}</dd>
                    </div>
                    <div className="detail-item">
                      <dt>お客様名（カナ）</dt>
                      <dd>{orderDetail.custNameKana || '-'}</dd>
                    </div>
                    {/* TODO: 鈴木さんからコメントアウト外すのを許可えたら外す */}
                    {/* <div className="detail-item">
                      <dt>出荷日</dt>
                      <dd>{orderDetail.shipDate || '-'}</dd>
                    </div> */}
                    <div className="detail-item">
                      <dt>注文種別</dt>
                      <dd>{orderDetail.orderType}</dd>
                    </div>
                  </dl>
                </div>

                {/* 注文商品セクション - コンタクトレンズ */}
                {orderDetail.orderClItems && orderDetail.orderClItems.length > 0 && (
                  <div className="detail-section">
                    <h2 className="section-title">注文商品</h2>
                    <table className="items-table">
                      <thead>
                        <tr>
                          <th>商品コード</th>
                          <th>商品名</th>
                          <th></th>
                          <th>FT (度数)</th>
                          <th>T.P (度数)</th>
                          <th>DIA</th>
                          {hasAstigmaProduct && <th>CYL (乱視度数)</th>}
                          <th>数量</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orderDetail.orderClItems.map((item) => (
                          <tr key={item.id}>
                            <td>{item.product.itemCode}</td>
                            <td>{item.product.name}</td>
                            <td>{item.eyeType}</td>
                            <td>{item.fc}</td>
                            <td>{item.tp}</td>
                            <td>{item.dia}</td>
                            {hasAstigmaProduct && <td>{item.cyl || '-'}</td>}
                            <td>{item.qty}枚</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* 注文商品セクション - ケア用品・販促品 */}
                {orderDetail.orderPromoCareItems && orderDetail.orderPromoCareItems.length > 0 && (
                  <div className="detail-section">
                    <h2 className="section-title">注文商品</h2>
                    <table className="items-table">
                      <thead>
                        <tr>
                          <th>商品コード</th>
                          <th>商品名</th>
                          <th>数量</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orderDetail.orderPromoCareItems.map((item) => (
                          <tr key={item.id}>
                            <td>{item.product.itemCode}</td>
                            <td>{item.product.name}</td>
                            <td>{item.qty}{item.quantityUnit}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="button-area">
                <button onClick={handleBack} className="btn-back">
                  一覧に戻る
                </button>
              </div>
            </div>
          </section>
        </main>
      </Layout>
    </React.Fragment>
  );
};

export default Detail;